import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isEqual } from 'lodash/lang';

import { StudySiteApi } from '../../../../../../api';
import Checkbox from '../../../../../../common/data-entry/InputSelectors/Checkbox';
import ModalBoxes from '../../../../../../common/feedback/ModalBoxes/ModalBoxes';
import Button from '../../../../../../common/general/Button';
import ButtonGroup from '../../../../../../common/general/ButtonGroup';
import { ROLE_SYSTEM_ADMINISTRATOR } from '../../../../../../constants/userRoles';
import { userHasRole } from '../../../../../../services/auth';
import { NSC_CONFIRMATIONS, NSPS_SITE } from '../../../../../../services/i18n/namespaces';

import { findCorrespondingLockNameForMetric, MAX_INPUT_VALUE, METRICS_VALUES_LIST } from './constants';

import './UpdateMetricsModal.scss';

function UpdateMetricsModal({ modalBox, initialMetrics, studySiteId }) {
  const { t } = useTranslation();
  const [metrics, setMetrics] = useState(initialMetrics);

  const handleLockChange = lockMetricName => {
    const prevLockValue = metrics[lockMetricName];
    setMetrics({ ...metrics, [lockMetricName]: !prevLockValue });
  };

  const handleValueChange = (metric, newValue) => {
    if (newValue === '') {
      setMetrics({ ...metrics, [metric]: newValue });
    } else if (/^\d+$/.test(newValue)) {
      const intValue = parseInt(newValue, 10);
      if (intValue < MAX_INPUT_VALUE / 10) {
        setMetrics({ ...metrics, [metric]: intValue });
      }
    }
  };

  const handleCancel = () => {
    if (!isEqual(metrics, initialMetrics)) {
      ModalBoxes.confirm({
        content: t('g01.descriptions', { ns: NSC_CONFIRMATIONS }),
        title: t('g01.title', { ns: NSC_CONFIRMATIONS }),
        confirmButton: t('actions.yes'),
        cancelButton: t('actions.no')
      })
        .then(() => {
          modalBox.close();
        })
        .catch(() => {});
    } else {
      modalBox.close();
    }
  };

  const handleUpdate = () => {
    StudySiteApi.saveOrUpdateMetrics(studySiteId, metrics).then(modalBox.close);
  };

  return (
    <>
      <ModalBoxes.Header onClose={handleCancel}>
        {t('expectedSsuSuccessMetrics.updateExpectedSsuSuccessMetrics', { ns: NSPS_SITE })}
      </ModalBoxes.Header>
      <ModalBoxes.Body>
        {METRICS_VALUES_LIST.map(metric => (
          <InputWithCheckBox
            key={metric}
            name={t(`expectedSsuSuccessMetrics.${metric}`, { ns: NSPS_SITE })}
            value={metrics[metric]}
            isLocked={metrics[findCorrespondingLockNameForMetric(metric)]}
            onLockChange={() => handleLockChange(findCorrespondingLockNameForMetric(metric))}
            onValueChange={e => handleValueChange(metric, e.target.value)}
          />
        ))}
      </ModalBoxes.Body>
      <ModalBoxes.Footer>
        <div className="eds-btn-group">
          <ButtonGroup>
            <Button priority="medium" onClick={handleCancel}>
              {t('actions.cancel')}
            </Button>
            <Button priority="high" onClick={handleUpdate}>
              {t('actions.update')}
            </Button>
          </ButtonGroup>
        </div>
      </ModalBoxes.Footer>
    </>
  );
}

function InputWithCheckBox({ name, value, isLocked, onValueChange, onLockChange }) {
  const { t } = useTranslation();
  return (
    <div className="form-group">
      <label className="metric-name">{name}</label>
      <div className="input-group">
        <input type="text" className="form-control" value={value || ''} onChange={onValueChange} disabled={isLocked} />
        <Checkbox
          className="lock-check-box"
          label={t('expectedSsuSuccessMetrics.locked', { ns: NSPS_SITE })}
          onChange={onLockChange}
          checked={isLocked}
          disabled={isLocked && !userHasRole(ROLE_SYSTEM_ADMINISTRATOR)}
        />
      </div>
    </div>
  );
}

UpdateMetricsModal.className = 'update-success-metrics-modal';
UpdateMetricsModal.size = 'w500';

export default UpdateMetricsModal;
